import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Raised when a step edit removes option values that existing rules depend on.
 *
 * A rule condition references its step and field by id, but stores the chosen option
 * as literal text — so renaming or deleting an option leaves the rule comparing
 * against text no answer will ever carry again, and it silently stops matching.
 *
 * This warns and nothing more: the rules are not touched either way.
 */

export type OptionImpactRuleCondition = {
  expression: string;
  value: string;
};

export type OptionImpactRule = {
  ruleUid: string;
  title: string;
  conditions: OptionImpactRuleCondition[];
};

export type OptionImpactEntry = {
  fieldUid: string;
  question: string | null;
  removed: string[];
  rules: OptionImpactRule[];
};

type OptionImpactDialogProps = {
  open: boolean;
  impacted: OptionImpactEntry[];
  onCancel: () => void;
  onConfirm: () => void;
};

export default function OptionImpactDialog({
  open,
  impacted,
  onCancel,
  onConfirm
}: OptionImpactDialogProps) {
  const ruleCount = impacted.reduce((total, entry) => total + entry.rules.length, 0);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
        Rules use this option
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {ruleCount === 1 ? '1 rule uses' : `${ruleCount} rules use`} an option you removed, and
          will stop working until you update {ruleCount === 1 ? 'it' : 'them'}.
        </Typography>

        {impacted.map((entry) => (
          <Box key={entry.fieldUid} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {entry.question || 'Field'}
            </Typography>

            {entry.rules.map((rule) => (
              <Typography key={rule.ruleUid} variant="body2" sx={{ mt: 0.5 }}>
                {rule.title || 'Untitled rule'}
                <Typography component="span" sx={{ color: 'warning.main', ml: 0.5 }} variant="body2">
                  {rule.conditions.map((condition) => `${condition.expression} ${condition.value}`).join(', ')}
                </Typography>
              </Typography>
            ))}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={onCancel} color="inherit" size="small">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="warning" size="small">
          Save Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
